import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { Plus, Trash2, MessageCircle, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";

export default function Messaging() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", platform: "whatsapp", trigger_keyword: "", message: "" };
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/templates"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.message) return toast.error("Missing fields");
    if (editing) await api.put(`/templates/${editing.id}`, form);
    else await api.post("/templates", form);
    toast.success("OK"); setOpen(false); setForm(empty); setEditing(null); load();
  };

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/templates/${id}`);
    load();
  };

  const openEdit = (it) => { setEditing(it); setForm(it); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="messaging-root">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Auto-reply</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("messaging")}</h1>
        </div>
        <button data-testid="open-new-template" onClick={openNew} className="btn-primary inline-flex items-center gap-2"><Plus size={16}/>{t("add_template")}</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.length === 0 && <div className="text-zinc-500 text-sm">{t("no_data")}</div>}
        {items.map((it) => {
          const Icon = it.platform === "whatsapp" ? MessageCircle : Send;
          const color = it.platform === "whatsapp" ? "#25D366" : "#0084FF";
          return (
            <div key={it.id} className="swiss-card p-5 cursor-pointer fade-up" onClick={() => openEdit(it)} data-testid={`tpl-${it.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 text-white" style={{background: color, borderRadius: 2}}>
                  <Icon size={10}/> {it.platform}
                </span>
                <button onClick={(e) => { e.stopPropagation(); del(it.id); }} className="text-zinc-400 hover:text-[#E63946]"><Trash2 size={14}/></button>
              </div>
              <div className="font-display font-bold text-lg mb-1">{it.name}</div>
              {it.trigger_keyword && <div className="text-xs text-zinc-500 mb-2">Trigger: <span className="font-mono bg-zinc-100 px-1">{it.trigger_keyword}</span></div>}
              <p className="text-sm text-zinc-600 line-clamp-3 whitespace-pre-wrap">{it.message}</p>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_template")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="tpl-name" placeholder={t("title")} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
            <select data-testid="tpl-platform" className="w-full border border-zinc-300 px-3 py-2 bg-white" style={{borderRadius: 2}} value={form.platform} onChange={(e) => setForm({...form, platform: e.target.value})}>
              <option value="whatsapp">{t("whatsapp")}</option>
              <option value="messenger">{t("messenger")}</option>
            </select>
            <input data-testid="tpl-trigger" placeholder={t("trigger_keyword")} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.trigger_keyword} onChange={(e) => setForm({...form, trigger_keyword: e.target.value})}/>
            <textarea data-testid="tpl-message" placeholder={t("message")} rows={6} className="w-full border border-zinc-300 px-3 py-2" style={{borderRadius: 2}} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}/>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300">{t("cancel")}</button>
            <button data-testid="tpl-save" onClick={save} className="btn-primary">{t("save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
